import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { FiRotateCcw } from "react-icons/fi";
import { MdPeopleOutline, MdAssignmentAdd } from "react-icons/md";
import { FaRegFilePdf, FaPrint, FaSearch } from "react-icons/fa";
import { FaRegEdit } from "react-icons/fa";
import { GrView } from "react-icons/gr";
import { LuPanelLeftOpen, LuPanelRightOpen } from "react-icons/lu";
import { FallingLines } from "react-loader-spinner";
import { usePDF } from "react-to-pdf";
import {
  FiberManualRecord as FiberManualRecordIcon,
  Pause as PauseIcon,
  WorkOutline as WorkOutlineIcon,
  SchemaOutlined as SchemaOutlinedIcon,
  Badge as BadgeIcon,
  PhoneOutlined as PhoneOutlinedIcon,
  EmailOutlined as EmailOutlinedIcon,
  GridView as GridViewIcon,
  TableRows as TableRowsIcon,
  ApartmentSharp as ApartmentSharpIcon,
} from "@mui/icons-material";
import { base_url as base_url_from_axios_config } from "../../utils/AxiosConfig";
import { base_url_image_view_api } from "../../utils/ImageViewingAPI";
import user1 from "../../images/ic_no_person_photo.svg";
import { getReadableDateDDMMYYYY } from "../../utils/utils";

// ─── Snackbar colours ──────────────────────────────────────────────────────────
const SNACK_STYLES = {
  info: { bar: "bg-blue-600", icon: "ℹ" },
  success: { bar: "bg-emerald-600", icon: "✓" },
  error: { bar: "bg-red-600", icon: "✕" },
  warning: { bar: "bg-amber-500", icon: "⚠" },
};

// ─── Custom Snackbar ───────────────────────────────────────────────────────────
const CustomSnackbar = ({ open, message, severity, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [open, onClose]);

  if (!open) return null;
  const s = SNACK_STYLES[severity] || SNACK_STYLES.info;

  return (
    <div className="fixed top-5 left-1/2 -translate-x-1/2 z-50 animate-slide-down">
      <div
        className={`${s.bar} flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-white text-sm font-medium min-w-[280px]`}
      >
        <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold flex-shrink-0">
          {s.icon}
        </span>
        <span className="flex-1">{message}</span>
        <button
          onClick={onClose}
          className="w-6 h-6 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors flex-shrink-0"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

// ─── Custom Tooltip ────────────────────────────────────────────────────────────
const Tooltip = ({ title, children }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div
      className="relative inline-flex"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible && title && (
        <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2.5 py-1.5 rounded-lg whitespace-nowrap shadow-lg z-50 pointer-events-none">
          {title}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-800 rotate-45" />
        </div>
      )}
    </div>
  );
};

// ─── Status Badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ active }) =>
  active ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-blue-600 text-white">
      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-slate-400 text-white">
      <PauseIcon style={{ fontSize: 10 }} />
      Inactive
    </span>
  );

// ─── Filter Select ─────────────────────────────────────────────────────────────
const FilterSelect = ({ label, value, onChange, options }) => (
  <div className="relative">
    <select
      value={value}
      onChange={onChange}
      className="appearance-none pl-3 pr-8 py-2 rounded-xl border border-slate-300 bg-white text-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 cursor-pointer min-w-[130px]"
    >
      <option value="">{label}</option>
      {options?.map((o) => (
        <option key={o.Description} value={o.Description}>
          {o.Description}
        </option>
      ))}
    </select>
    <svg
      className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  </div>
);

// ─── Pagination ────────────────────────────────────────────────────────────────
const Pagination = ({
  currentPage,
  npage,
  numbers,
  prePage,
  nextPage,
  changeCPage,
}) => {
  const MAX_VISIBLE = 7;
  const renderNumbers = () => {
    if (numbers.length <= MAX_VISIBLE) return numbers;
    const pages = new Set([1, npage, currentPage]);
    if (currentPage > 1) pages.add(currentPage - 1);
    if (currentPage < npage) pages.add(currentPage + 1);
    return [...pages].sort((a, b) => a - b);
  };

  const visiblePages = renderNumbers();
  let lastPage = null;

  return (
    <div className="flex items-center justify-center gap-1.5 py-4">
      <button
        onClick={prePage}
        disabled={currentPage === 1}
        className="px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
      >
        ← Prev
      </button>

      {visiblePages.map((n) => {
        const showEllipsis = lastPage && n - lastPage > 1;
        lastPage = n;
        return (
          <React.Fragment key={n}>
            {showEllipsis && (
              <span className="px-2 text-slate-400 text-sm">…</span>
            )}
            <button
              onClick={() => changeCPage(n)}
              className={`w-9 h-9 rounded-lg text-sm font-semibold transition-all duration-150 ${
                currentPage === n
                  ? "bg-blue-600 text-white shadow-md shadow-blue-200"
                  : "border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {n}
            </button>
          </React.Fragment>
        );
      })}

      <button
        onClick={nextPage}
        disabled={currentPage === npage}
        className="px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
      >
        Next →
      </button>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const Employee = () => {
  const storedUserDataArray =
    JSON.parse(localStorage.getItem("BondTimeuserDataArray")) || [];
  const companyCode = storedUserDataArray[0]?.cmpcode || null;
  const [base_url] = useState(base_url_from_axios_config);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toPDF, targetRef } = usePDF({ filename: "Emplist.pdf" });

  // ── State (UNCHANGED) ────────────────────────────────────────────────────────
  const [data, setData] = useState(null);
  const [searchdata, setSearchData] = useState("");
  const [loader, setLoader] = useState(true);
  const [Division, setDivision] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState("");
  const [grade, setGrade] = useState(null);
  const [selectedgrade, setSelectedGrade] = useState("");
  const [jobtitle, setJobTitle] = useState(null);
  const [selectedJob, setSelectedJobTitle] = useState("");
  const [jobMUI, setJobMUI] = useState("");
  const [gradeMUI, setGradeMUI] = useState("");
  const [divisionMUI, setDivisionMUI] = useState("");
  const [resetalldata, setResetallData] = useState(false);
  const [Open, setOpen] = useState(false);
  const [dataView, setDataView] = useState("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [isActive, setIsActive] = useState("YES");

  const recordsPerPage = 20;
  const lastIndex = currentPage * recordsPerPage;
  const firstIndex = lastIndex - recordsPerPage;
  const records = data?.slice(firstIndex, lastIndex);
  const npage = Math.ceil(data?.length / recordsPerPage);
  const numbers = Array.from({ length: npage }, (_, i) => i + 1);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const showSnackbar = (message, severity) =>
    setSnackbar({ open: true, message, severity });
  const closeSnackbar = () => setSnackbar((p) => ({ ...p, open: false }));

  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (e) => {
    e.preventDefault();
    setAnchorEl(e.currentTarget);
    return false;
  };
  const handleClose = () => setAnchorEl(null);

  // ── Profile pic (UNCHANGED) ──────────────────────────────────────────────────
  const fetchProfilePic = (ImagePath) => {
    if (!ImagePath) return user1;
    const filePath = ImagePath.replace(/\\/g, "/");
    const fileName = filePath.split("/").pop();
    return `${base_url_image_view_api}/BONDTIME_EMP_IMG/${fileName}`;
  };

  // ── Filters (UNCHANGED) ─────────────────────────────────────────────────────
  const handleChange = (event, eventType) => {
    const value = event.target.value;
    switch (eventType) {
      case "jobTitle":
        setSelectedJobTitle(value);
        setJobMUI(value);
        break;
      case "grade":
        setSelectedGrade(value);
        setGradeMUI(value);
        break;
      case "division":
        setSelectedDivision(value);
        setDivisionMUI(value);
        break;
    }
    setSearchParams(
      new URLSearchParams({
        jobTitle: eventType === "jobTitle" ? value : selectedJob,
        grade: eventType === "grade" ? value : selectedgrade,
        division: eventType === "division" ? value : selectedDivision,
      }).toString(),
    );
  };

  // ── Data fetch (UNCHANGED) ───────────────────────────────────────────────────
  const getData = async () => {
    setLoader(true);
    try {
      const jobTitleFromQuery = searchParams.get("jobTitle") || "";
      const gradeFromQuery = searchParams.get("grade") || "";
      const divisionFromQuery = searchParams.get("division") || "";

      const isValidJobTitle =
        jobTitleFromQuery &&
        jobtitle?.some((j) => j.Description === jobTitleFromQuery);
      const isValidGrade =
        gradeFromQuery && grade?.some((g) => g.Description === gradeFromQuery);
      const isValidDivision =
        divisionFromQuery &&
        Division?.some((d) => d.Description === divisionFromQuery);

      if (isValidJobTitle && jobTitleFromQuery !== selectedJob) {
        setSelectedJobTitle(jobTitleFromQuery);
        setJobMUI(jobTitleFromQuery);
      }
      if (isValidGrade && gradeFromQuery !== selectedgrade) {
        setSelectedGrade(gradeFromQuery);
        setGradeMUI(gradeFromQuery);
      }
      if (isValidDivision && divisionFromQuery !== selectedDivision) {
        setSelectedDivision(divisionFromQuery);
        setDivisionMUI(divisionFromQuery);
      }

      const getParam = (reset, qValid, qVal, sel) =>
        reset ? "ALL" : qValid ? qVal : sel || "ALL";
      const jobTitleParam = getParam(
        resetalldata,
        isValidJobTitle,
        jobTitleFromQuery,
        selectedJob,
      );
      const gradeParam = getParam(
        resetalldata,
        isValidGrade,
        gradeFromQuery,
        selectedgrade,
      );
      const divisionParam = getParam(
        resetalldata,
        isValidDivision,
        divisionFromQuery,
        selectedDivision,
      );
      const searchParam = resetalldata ? "ALL" : searchdata || "ALL";
      const searchType = searchdata && !resetalldata ? "FREETEXT" : "ALL";

      const response = await axios.get(
        `${base_url}/api/PersonalInfoList/${companyCode}/${searchType}/${isActive}/${searchParam}/${jobTitleParam}/${gradeParam}/${divisionParam}`,
      );

      if (response?.data) {
        setData(response.data);
        setCurrentPage(1);
        if (
          (jobTitleFromQuery || gradeFromQuery || divisionFromQuery) &&
          !resetalldata
        )
          showSnackbar("Filters applied from previous page", "info");
      } else {
        showSnackbar("No employee data found", "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Error fetching employee data", "error");
    } finally {
      setLoader(false);
      if (resetalldata) setResetallData(false);
    }
  };

  useEffect(() => {
    if (base_url && companyCode && jobtitle && grade && Division) getData();
  }, [
    base_url,
    companyCode,
    searchParams,
    searchdata,
    selectedJob,
    selectedgrade,
    selectedDivision,
    resetalldata,
    jobtitle,
    grade,
    Division,
    isActive,
  ]);

  // Master data (UNCHANGED)
  useEffect(() => {
    if (!base_url || !companyCode) return;
    axios
      .get(`${base_url}/api/MASTERLIST/${companyCode}/DIVISION/-`)
      .then((r) => r?.data && setDivision(r.data))
      .catch(console.error);
  }, [base_url, companyCode]);
  useEffect(() => {
    if (!base_url || !companyCode) return;
    axios
      .get(`${base_url}/api/MasterList/${companyCode}/JOBTITLE/-`)
      .then((r) => r?.data && setJobTitle(r.data))
      .catch(console.error);
  }, [base_url, companyCode]);
  useEffect(() => {
    if (!base_url || !companyCode) return;
    axios
      .get(`${base_url}/api/MASTERLIST/${companyCode}/GRADE/-`)
      .then((r) => r?.data && setGrade(r.data))
      .catch(console.error);
  }, [base_url, companyCode]);

  // ── Handlers (UNCHANGED) ─────────────────────────────────────────────────────
  const handleReset = () => {
    setResetallData(true);
    setSearchData("");
    setSelectedJobTitle("");
    setSelectedGrade("");
    setSelectedDivision("");
    setJobMUI("");
    setGradeMUI("");
    setDivisionMUI("");
    setSearchParams("");
  };
  const navigateToEdit = (empId) => {
    navigate(
      `/admin/emp_edit/${encodeURIComponent(empId)}?${new URLSearchParams({ jobTitle: selectedJob || "", grade: selectedgrade || "", division: selectedDivision || "" })}`,
    );
  };
  const printEmpList = () =>
    navigate("/admin/employeeListReport", { state: { emplistData: records } });
  const prePage = () => currentPage !== 1 && setCurrentPage(currentPage - 1);
  const nextPage = () =>
    currentPage !== npage && setCurrentPage(currentPage + 1);
  const changeCPage = (id) => setCurrentPage(id);

  const formatDateWithExpiry = (date) => {
    if (!date) return <span>-</span>;
    const parsed = new Date(date);
    if (isNaN(parsed)) return <span>-</span>;
    const isExpired = parsed < new Date();
    return (
      <span className={isExpired ? "text-red-500 font-semibold" : ""}>
        {parsed.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })}
      </span>
    );
  };

  // ─── Expanded table columns ─────────────────────────────────────────────────
  const EXTRA_HEADERS = [
    "Bank Account",
    "Bank Name",
    "Agent Id",
    "Current Address",
    "Home Address",
    "Home Airport",
    "Work Mobile",
    "Work Phone",
    "Personal Phone",
    "Personal Email",
    "Passport Number",
    "Passport Issue Date",
    "Passport Expiry Date",
    "Passport Place of Issue",
    "Passport Country of Issue",
    "Emirates Id",
    "Expiry Date",
    "Visa No",
    "Visa Expiry Date",
    "Insurance Number",
    "Insurance Expiry Date",
    "Driving License No",
    "Driving License Expiry Date",
    "Basic Pay",
    "Allowances OT",
    "Allowances NOT",
    "Normal HRS",
    "Hourly Wage",
    "OT Rate",
    "Spl OT Rate",
    "Active",
    "Memos",
    "Notes",
    "Salary Ac",
    "Advance Ac",
  ];

  const getExtraValues = (d) => [
    d?.BankAccountNo,
    d?.BankName,
    d?.AgentID,
    d?.CurrentAddress,
    d?.HomeAddress,
    d?.HomeAirport,
    d?.WorkMobile,
    d?.WorkPhone,
    d?.PersonalPhone,
    d?.PersonalEmail,
    d?.PassportNo,
    formatDateWithExpiry(d?.PassportIssueDate),
    formatDateWithExpiry(d?.PassportExpiryDate),
    d?.PassportPlaceofIssue,
    d?.PassportCountryofIssue,
    d?.EmiratesId,
    formatDateWithExpiry(d?.EmiratesIdExpiryDate),
    d?.VisaNo,
    formatDateWithExpiry(d?.VisaExpiryDate),
    d?.InsuranceNo,
    formatDateWithExpiry(d?.InsuranceExpiryDate),
    d?.DrivingLicenceNo,
    formatDateWithExpiry(d?.DrivingLicenceExpiryDate),
    d?.BasicPay,
    d?.Allowances_OT,
    d?.Allowances_NOT,
    d?.NormalHrs,
    d?.HourlyWage,
    d?.OTrate,
    d?.SplOtRate,
    d?.Active,
    d?.Memos,
    d?.Notes,
    d?.SalaryA,
    d?.AdvanceAc,
  ];

  const [inactiveEmployee, setInactiveEmployee] = useState(null);
  const [activating, setActivating] = useState(false);


  const handleCardClick = (item) => {
    console.log("IsActive employee:", item?.Active?.toLowerCase());
    if (item?.Active?.trim().toLowerCase() === "no") {
      setInactiveEmployee(item);
      console.log("Inactive Employee ID:", item?.EmpId);
      console.log("Inactive Employee status:", item?.Active?.toLowerCase());
      return;
    }

    navigate(
      `emp_edit/${encodeURIComponent(item?.EmpId)}?jobTitle=${encodeURIComponent(
        selectedJob || "",
      )}&grade=${encodeURIComponent(
        selectedgrade || "",
      )}&division=${encodeURIComponent(selectedDivision || "")}`,
    );
  };

  const toggleEmpStatus = async (empId, currentStatus) => {
    console.log("empId:", empId);
    console.log("currentStatus:", currentStatus);

    try {
      setActivating(true);
      const storeduserDataArray =
        JSON.parse(localStorage.getItem("BondTimeuserDataArray")) || [];

      const companyCode = storeduserDataArray?.[0]?.cmpcode || "";

      await axios.get(
        // `${base_url}/api/CRMStatusUpdate/${companyCode}/EMPACTIVE/${empId}/${currentStatus}`,
        `${base_url}/api/CRMStatusUpdate/${companyCode}/EMPACTIVE/${empId}/Yes`,
      );

      console.log("updated employee status to active");

      getData();
      // setSnackbar({
      //   open: true,
      //   message: `Team ${
      //     newStatus === "Y" ? "activated" : "deactivated"
      //   } successfully`,
      //   severity: "success",
      // });
    } catch (error) {
      console.error("Error updating employee inactive status:", error);

      // setSnackbar({
      //   open: true,
      //   message: "Failed to update team status",
      //   severity: "error",
      // });
    } finally {
      setActivating(false);
    }
  };

  // ─── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div className="h-screen overflow-y-auto bg-slate-50 px-4 sm:px-6 lg:px-8 py-6">
      <div className="mx-auto max-w-screen-2xl space-y-5">
        {/* ── Page Header ──────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-md">
              <MdPeopleOutline className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">
                Employee List
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                {data ? `${data.length} employees` : "Loading…"} &middot;{" "}
                {isActive === "YES" ? "Active" : "Inactive"}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={printEmpList}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-all shadow-sm"
            >
              <FaPrint className="w-3.5 h-3.5" /> Print
            </button>
            <button
              onClick={() => toPDF()}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100 transition-all shadow-sm"
            >
              <FaRegFilePdf className="w-3.5 h-3.5" /> Export PDF
            </button>
            <button
              onClick={() => navigate("emp_reg")}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-all shadow-sm"
            >
              <MdAssignmentAdd className="w-4 h-4" /> New Employee
            </button>
          </div>
        </div>

        {/* ── Filter Bar ───────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            {/* Left: Active / Inactive toggle */}
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl w-fit">
              {[
                { label: "Active", val: "YES" },
                { label: "Inactive", val: "NO" },
              ].map(({ label, val }) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setIsActive(val)}
                  className={`px-5 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    isActive === val
                      ? "bg-blue-600 text-white shadow-md"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Right: Search + Filters + Reset + View Toggle */}
            <div className="flex flex-wrap items-center gap-2.5">
              {/* Search */}
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all min-w-[200px]">
                <FaSearch className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Search by ID or Name…"
                  value={searchdata}
                  onChange={(e) => setSearchData(e.target.value)}
                  className="flex-1 bg-transparent outline-none text-sm text-slate-700 placeholder-slate-400"
                />
                {searchdata && (
                  <button
                    onClick={() => setSearchData("")}
                    className="text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>

              {/* Dropdowns */}
              <FilterSelect
                label="Job Title"
                value={jobMUI}
                onChange={(e) => handleChange(e, "jobTitle")}
                options={jobtitle}
              />
              <FilterSelect
                label="Grade"
                value={gradeMUI}
                onChange={(e) => handleChange(e, "grade")}
                options={grade}
              />
              <FilterSelect
                label="Division"
                value={divisionMUI}
                onChange={(e) => handleChange(e, "division")}
                options={Division}
              />

              {/* Reset */}
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-300 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-all"
              >
                <FiRotateCcw className="w-3.5 h-3.5" /> Reset
              </button>

              {/* View Toggle */}
              <div className="flex items-center bg-slate-100 p-1 rounded-xl gap-1">
                <Tooltip title="Grid View">
                  <button
                    onClick={() => setDataView("grid")}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150 ${
                      dataView === "grid"
                        ? "bg-blue-600 text-white shadow-md"
                        : "text-slate-400 hover:text-slate-600"
                    }`}
                  >
                    <GridViewIcon style={{ fontSize: 16 }} />
                  </button>
                </Tooltip>
                <Tooltip title="Table View">
                  <button
                    onClick={() => setDataView("table")}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150 ${
                      dataView === "table"
                        ? "bg-blue-600 text-white shadow-md"
                        : "text-slate-400 hover:text-slate-600"
                    }`}
                  >
                    <TableRowsIcon style={{ fontSize: 16 }} />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>

        {/* ── Results Count ────────────────────────────────────────────────── */}
        {!loader && data && (
          <p className="text-xs text-slate-400 px-1">
            Showing{" "}
            <span className="font-semibold text-slate-600">
              {records?.length ?? 0}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-slate-600">{data.length}</span>{" "}
            employees
          </p>
        )}

        {/* ── Table View ───────────────────────────────────────────────────── */}
        {dataView === "table" && !loader && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Expand/Collapse columns toggle */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Employee Records
              </p>
              <button
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-500 text-xs font-semibold hover:bg-slate-50 transition-all"
              >
                {Open ? (
                  <>
                    <LuPanelRightOpen className="w-3.5 h-3.5" /> Collapse
                    Columns
                  </>
                ) : (
                  <>
                    <LuPanelLeftOpen className="w-3.5 h-3.5" /> Expand All
                    Columns
                  </>
                )}
              </button>
            </div>

            <div className="overflow-auto max-h-[60vh]" ref={targetRef}>
              <table className="min-w-full border-separate border-spacing-0 text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-blue-600 text-white">
                    {[
                      "#",
                      "Emp ID",
                      "Alt ID",
                      "Status",
                      "Name",
                      "Job Title",
                      "Division",
                      "Grade",
                      "Gender",
                      "Nationality",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-3 py-3 text-left font-semibold text-xs uppercase tracking-wide whitespace-nowrap first:rounded-none"
                      >
                        {h}
                      </th>
                    ))}
                    {Open &&
                      EXTRA_HEADERS.map((h) => (
                        <th
                          key={h}
                          className="px-3 py-3 text-left font-semibold text-xs uppercase tracking-wide whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    <th className="px-3 py-3 text-center font-semibold text-xs uppercase tracking-wide">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {records?.map((d, i) => (
                    <tr
                      key={i}
                      className={`transition-colors duration-100 hover:bg-blue-50 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}`}
                    >
                      <td className="px-3 py-2.5 text-slate-400 font-medium whitespace-nowrap border-b border-slate-100">
                        {firstIndex + i + 1}
                      </td>
                      <td className="px-3 py-2.5 font-semibold text-slate-700 whitespace-nowrap border-b border-slate-100">
                        {d?.EmpId}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap border-b border-slate-100">
                        {d?.AlternateID || "-"}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap border-b border-slate-100">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold ${
                            d?.Status?.toLowerCase() === "active"
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-slate-100 text-slate-600 border border-slate-200"
                          }`}
                        >
                          {d?.Status || "-"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap border-b border-slate-100">
                        <div className="flex items-center gap-2">
                          <img
                            className="w-7 h-7 rounded-full object-cover ring-2 ring-white shadow-sm"
                            src={
                              d?.imagepath
                                ? fetchProfilePic(d.imagepath)
                                : user1
                            }
                            alt=""
                          />
                          <span className="font-medium text-slate-800">
                            {d?.Name}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap border-b border-slate-100">
                        {d?.JobTitle || "-"}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap border-b border-slate-100">
                        {d?.Division || "-"}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap border-b border-slate-100">
                        {d?.Grade || "-"}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap border-b border-slate-100">
                        {d?.Single || "-"}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap border-b border-slate-100">
                        {d?.Nationality || "-"}
                      </td>

                      {Open &&
                        getExtraValues(d).map((val, idx) => (
                          <td
                            key={idx}
                            className="px-3 py-2.5 text-slate-500 whitespace-nowrap border-b border-slate-100 max-w-[160px] truncate"
                          >
                            {val || "-"}
                          </td>
                        ))}

                      <td className="px-3 py-2.5 whitespace-nowrap border-b border-slate-100">
                        <div className="flex items-center justify-center">
                          <Link
                            onClick={() => navigateToEdit(d?.EmpId)}
                            to={`emp_edit/${encodeURIComponent(d?.EmpId)}?jobTitle=${encodeURIComponent(selectedJob || "")}&grade=${encodeURIComponent(selectedgrade || "")}&division=${encodeURIComponent(selectedDivision || "")}`}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                          >
                            <FaRegEdit className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Empty state */}
              {!loader && records?.length === 0 && (
                <div className="flex flex-col items-center py-16">
                  <MdPeopleOutline className="w-12 h-12 text-slate-200 mb-3" />
                  <p className="text-slate-400 font-medium">
                    No employees found
                  </p>
                  <p className="text-slate-300 text-sm mt-1">
                    Try adjusting your filters
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Grid View ────────────────────────────────────────────────────── */}
        {dataView === "grid" && !loader && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {records?.map((item, index) => (
              // <Link
              //   key={index}
              //   to={`emp_edit/${encodeURIComponent(item?.EmpId)}?jobTitle=${encodeURIComponent(selectedJob || "")}&grade=${encodeURIComponent(selectedgrade || "")}&division=${encodeURIComponent(selectedDivision || "")}`}
              //   onClick={() => navigateToEdit(item?.EmpId)}
              //   className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-200 transition-all duration-200 overflow-hidden flex flex-col"
              // >

              <div
                key={index}
                onClick={() => handleCardClick(item)}
                className="group cursor-pointer bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-200 transition-all duration-200 overflow-hidden flex flex-col"
              >
                {/* Card top colour strip */}
                <div
                  className={`h-1 w-full ${item.Active?.toLowerCase() === "yes" ? "bg-gradient-to-r from-blue-500 to-blue-600" : "bg-gradient-to-r from-slate-300 to-slate-400"}`}
                />

                <div className="flex flex-col items-center px-4 pt-5 pb-4">
                  {/* Avatar + Status badge */}
                  <div className="relative mb-3  w-270 p-8">
                    <img
                      className="w-[72px] h-[72px] rounded-full object-cover ring-4 ring-white shadow-md"
                      src={
                        item?.imagepath
                          ? fetchProfilePic(item.imagepath)
                          : user1
                      }
                      alt={item.Name}
                    />
                    <div className="absolute top-5 -right-1">
                      <StatusBadge
                        active={item.Active?.toLowerCase() === "yes"}
                      />
                    </div>
                  </div>

                  {/* Name & Job Title */}
                  <p className="text-sm font-bold text-slate-900 text-center leading-tight line-clamp-1">
                    {item.Name}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5 text-center line-clamp-1">
                    {item.JobTitle || "—"}
                  </p>

                  {/* Info rows */}
                  <div className="w-full mt-3 rounded-xl bg-slate-50 border border-slate-100 divide-y divide-slate-100 text-xs">
                    {[
                      {
                        icon: <SchemaOutlinedIcon style={{ fontSize: 12 }} />,
                        val: item.Division,
                      },
                      {
                        icon: <EmailOutlinedIcon style={{ fontSize: 12 }} />,
                        val: item.WorkEmail,
                      },
                      {
                        icon: <PhoneOutlinedIcon style={{ fontSize: 12 }} />,
                        val: item.WorkMobile,
                      },
                      {
                        icon: <WorkOutlineIcon style={{ fontSize: 12 }} />,
                        val: item.JobType,
                      },
                    ].map(({ icon, val }, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 px-3 py-1.5"
                      >
                        <span className="text-slate-400 flex-shrink-0">
                          {icon}
                        </span>
                        {val ? (
                          <span className="text-slate-600 truncate">{val}</span>
                        ) : (
                          <span className="inline-block h-2 w-16 bg-slate-200 rounded animate-pulse" />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Badges row */}
                  <div className="flex flex-wrap items-center justify-center gap-1.5 mt-3">
                    {/* EmpId badge */}
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-sky-100 text-sky-700 border border-sky-200">
                      <BadgeIcon style={{ fontSize: 10 }} />
                      {item.EmpId}
                    </span>
                    {/* Division badge */}
                    {item.Division ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-rose-50 text-rose-600 border border-rose-200">
                        <ApartmentSharpIcon style={{ fontSize: 10 }} />
                        {item.Division}
                      </span>
                    ) : (
                      <span className="inline-block h-4 w-20 bg-rose-100 rounded-full animate-pulse" />
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between w-full mt-3 pt-2.5 border-t border-slate-100">
                    <span className="text-slate-400 text-[10px]">
                      Joined {getReadableDateDDMMYYYY(item.JoinDate)}
                    </span>
                    <span className="text-blue-600 text-[10px] font-semibold group-hover:underline">
                      View Details →
                    </span>
                  </div>
                </div>
                {/* </Link> */}
              </div>
            ))}

            {/* Empty grid state */}
            {records?.length === 0 && (
              <div className="col-span-full flex flex-col items-center py-20">
                <MdPeopleOutline className="w-14 h-14 text-slate-200 mb-3" />
                <p className="text-slate-400 font-semibold">
                  No employees found
                </p>
                <p className="text-slate-300 text-sm mt-1">
                  Try adjusting your search or filters
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── Loader ───────────────────────────────────────────────────────── */}
        {loader && (
          <div className="flex flex-col items-center justify-center py-24">
            <FallingLines color="#2563eb" width="60" visible />
            <p className="mt-3 text-sm text-slate-400 font-medium">
              Loading employees…
            </p>
          </div>
        )}

        {/* ── Pagination ───────────────────────────────────────────────────── */}
        {records && npage > 1 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-4">
            <Pagination
              currentPage={currentPage}
              npage={npage}
              numbers={numbers}
              prePage={prePage}
              nextPage={nextPage}
              changeCPage={changeCPage}
            />
          </div>
        )}
      </div>

      {/* ── Custom Snackbar ───────────────────────────────────────────────── */}
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={closeSnackbar}
      />

      {inactiveEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <span className="text-xl">⚠️</span>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-800">
                  Inactive Employee
                </h3>
                <p className="text-sm text-slate-500">
                  This employee is currently inactive.
                </p>
              </div>
            </div>

            <p className="text-slate-600 mb-6">
              Do you want to toggle this employee back to <b>Active</b>?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setInactiveEmployee(null)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  toggleEmpStatus(
                    inactiveEmployee.EmpId,
                    inactiveEmployee.Active,
                  );
                  setInactiveEmployee(null);
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Activate Employee
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employee;
